import React from 'react';
import { Stack, Box, Typography, Tooltip, Fade } from '@mui/material';
import { styled } from '@mui/system';
import { Palette } from 'Palette';
import { determineNextTone } from '../styles/styles';

export interface StepStatusBarProps<T extends string> {
  statuses: T[];
  activeStatus: T;
  onPromote: (toStatus: T) => void;
  onStepClick?: (toStatus: T) => void; // New prop for backward navigation
  labelMap?: Partial<Record<T, string>>;
  tone?: keyof typeof Palette;
  className?: string;
  canPromote: boolean;
  allowBackward?: boolean; // New prop to enable backward navigation
}

interface StepStatusBarState {
  animateKey: number;
}

const Chevron = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'clickable' && prop !== 'tone',
})<{
  active?: boolean;
  clickable?: boolean;
  tone: keyof typeof Palette;
  className?: string;
}>(({ active, clickable, tone }) => {
  // Enhanced UX Logic: Determine visual state based on position relative to current
  const isNextStep = clickable && !active;
  const isFutureStep = !clickable && !active && tone !== 'active';

  let backgroundColor, textColor, borderStyle, opacity, fontWeight;

  if (active) {
    // Current status - maximum prominence
    backgroundColor = Palette.active.background;
    textColor = Palette.active.text;
    borderStyle = `inset 0 0 0 2px ${Palette.active.border}`;
    opacity = 1;
    fontWeight = 700;
  } else if (isNextStep) {
    // Next clickable status - interactive with subtle background
    backgroundColor = 'rgba(0, 123, 255, 0.08)'; // Light blue tint
    textColor = '#0056b3';
    borderStyle = '1px solid rgba(0, 123, 255, 0.2)';
    opacity = 1;
    fontWeight = 500;
  } else if (isFutureStep) {
    // Future statuses - neutral, de-emphasized
    backgroundColor = '#f8f9fa'; // Light neutral grey
    textColor = '#6c757d'; // Muted text
    borderStyle = '1px solid #e9ecef';
    opacity = 0.7;
    fontWeight = 400;
  } else {
    // Past/completed statuses
    backgroundColor = '#e8f5e8'; // Light green tint
    textColor = '#495057';
    borderStyle = '2px solid #28a745';
    opacity = 0.85;
    fontWeight = 400;
  }

  return {
    minWidth: 100,
    padding: '10px 20px',
    clipPath:
      'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)',
    background: backgroundColor,
    color: textColor,
    textAlign: 'center',
    flex: 1,
    transition: 'background 0.3s ease, color 0.3s ease, border 0.3s ease',
    cursor: clickable ? 'pointer' : 'default',
    opacity: opacity,
    fontWeight: fontWeight,
    textTransform: 'uppercase',
    boxShadow: borderStyle,
    position: 'relative',
    className: `react-StepStatusBar-Chevron ${active ? 'active' : ''} ${isNextStep ? 'next' : ''} ${isFutureStep ? 'future' : ''}`,
    zIndex: 0,

    '&:hover': {
      // Enhanced hover effects based on status type
      background: isNextStep
        ? 'rgba(0, 123, 255, 0.15)' // Stronger blue for next step
        : isFutureStep
          ? '#f1f3f4' // Subtle change for future steps
          : backgroundColor, // No change for past/current
      color: isNextStep
        ? '#004085' // Darker blue text on hover
        : textColor,
      transform: clickable ? 'scale(1.02)' : 'none',
      zIndex: clickable ? 1 : 0,
    },

    '&::after': isNextStep
      ? {
          content: '"→"',
          position: 'absolute',
          top: '50%',
          right: '-8px',
          transform: 'translateY(-50%)',
          fontSize: '16px',
          color: '#0056b3',
          fontWeight: 'bold',
          zIndex: 2,
          pointerEvents: 'none',
        }
      : {},
  };
});
export default class StepStatusBar<T extends string> extends React.Component<
  StepStatusBarProps<T>,
  StepStatusBarState
> {
  static defaultProps = {
    tone: 'default',
  };

  constructor(props: StepStatusBarProps<T>) {
    super(props);
    this.state = { animateKey: Date.now() };
  }

  componentDidUpdate(prevProps: StepStatusBarProps<T>) {
    if (prevProps.activeStatus !== this.props.activeStatus) {
      this.setState({ animateKey: Date.now() });
    }
  }

  getTone(index: number): keyof typeof Palette {
    const { tone = 'default' } = this.props;
    return determineNextTone(tone, index);
  }

  render() {
    const {
      statuses,
      activeStatus,
      onPromote,
      onStepClick,
      labelMap = {},
      className,
      canPromote,
      allowBackward = false,
    } = this.props;
    const { animateKey } = this.state;

    const currentIndex = statuses.indexOf(activeStatus);

    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        sx={{ width: '100%', mb: 3 }}
        className={`react-StepStatusBar ${className || ''}`.trim()}
      >
        {statuses.map((status, index) => {
          const isCurrent = index === currentIndex;
          const isNext = index === currentIndex + 1;
          const isPrevious = index < currentIndex && allowBackward;
          const isClickable = (isNext && canPromote) || isPrevious;
          const tone = this.getTone(index);
          const label = (labelMap as Record<T, string>)[status] || status;

          let tooltip = '';
          if (isNext && canPromote) {
            tooltip = `Promote to ${label}`;
          } else if (isPrevious) {
            tooltip = `Go back to ${label}`;
          }

          const handleClick = () => {
            if (isNext && canPromote) {
              onPromote(status);
            } else if (isPrevious && onStepClick) {
              onStepClick(status);
            }
          };

          return (
            <Tooltip key={status} title={tooltip || ''} disableHoverListener={!tooltip}>
              <Fade in timeout={400} key={`${animateKey}-${status}`}>
                <Chevron
                  active={isCurrent}
                  clickable={isClickable}
                  tone={tone}
                  onClick={handleClick}
                >
                  <Typography variant="body2">{label}</Typography>
                </Chevron>
              </Fade>
            </Tooltip>
          );
        })}
      </Stack>
    );
  }
}
